/**
 * Mapbox GL JS Configuration
 *
 * Core settings for the hybrid Mapbox implementation.
 * Uses custom satellite tiles hosted on Cloudflare R2 with Mapbox's base map styles.
 */

import type mapboxgl from "mapbox-gl";

export const MAPBOX_CONFIG = {
  /**
   * Mapbox access token from environment variables
   * Should be a public token with URL restrictions
   */
  accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,

  /**
   * Base map style - using blank style so only custom tiles show
   * This removes all labels, borders, and other Mapbox layers
   *
   * Note: Includes glyphs for symbol/text layers to work
   *
   * Alternative options if you want labels back:
   * - 'mapbox://styles/mapbox/dark-v11' (dark theme with labels)
   * - 'mapbox://styles/mapbox/satellite-v9' (satellite with labels)
   */
  style: {
    version: 8,
    sources: {},
    layers: [] as mapboxgl.AnyLayer[],
    // Required for symbol/text layers
    glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
  } as mapboxgl.Style /**
   * Custom satellite tiles configuration
   * These are NASA Earth at Night tiles processed and hosted on Cloudflare R2
   */,
  customTiles: {
    sourceId: "night-lights",
    layerId: "night-earth",
    tilesUrl: "https://map-tiles.lumi.theforge.space/{z}/{x}/{y}.png",
    tileSize: 256,
    maxZoom: 8,
  },

  /**
   * Default map view settings
   * Center: [longitude, latitude]
   */
  defaultView: {
    center: [0, 30] as [number, number], // Centered on equator, slight north bias
    zoom: 1.5,
    minZoom: 2.5, // Prevent zooming out too far (would show multiple Earths)
    maxZoom: 6.5, // Matches our tile data max zoom
  },

  /**
   * Map projection - using flat Mercator instead of 3D globe
   * Other options: 'globe', 'albers', 'equalEarth', 'naturalEarth'
   */
  projection: {
    name: "mercator",
  },
  /**
   * Enable infinite horizontal tiling (world repeats when panning horizontally)
   * This is a key feature - allows seamless wraparound without edge artifacts
   */
  renderWorldCopies: true,

  /**
   * Route-specific opacity settings
   * These control how visible the satellite imagery is on different pages
   */
  opacity: {
    homepage: 1,
    browse: 1,
    titlePage: 0.1,
    signin: 1,
    signup: 1,
  },

  /**
   * Route-specific zoom settings
   */
  zoom: {
    homepage: 3, // Slightly more zoomed in on home for better detail
    browse: 1.5, // Default zoom for browse pages
  },

  /**
   * Route-specific center settings
   */
  center: {
    homepage: [0, 40] as [number, number], // North bias for homepage
    default: [0, 0] as [number, number], // Equator for other routes
  },

  /**
   * Country boundaries configuration for interactive map
   * Uses Mapbox's built-in country-boundaries-v1 tileset
   */
  countryBoundaries: {
    sourceId: "country-boundaries",
    sourceLayer: "country_boundaries",
    fillLayerId: "country-fills",
    borderLayerId: "country-borders",

    // Styling for country glow effect (based on content density)
    fillColor: "#F3B33B", // Accent color from design tokens
    borderColor: "#444444", // Default border color
    borderHoverColor: "#F3B33B", // Hover border color (accent)
    borderWidth: 1,

    // Opacity range for content density visualization
    minOpacity: 0.2, // Countries with few films
    maxOpacity: 0.9, // Countries with many films
  },

  /**
   * Animation settings
   */
  animation: {
    // Slow pan for homepage ambient motion
    slowPan: {
      degreesPerInterval: -2.5, // Move 1.5° longitude per interval
      intervalMs: 10000, // Every 10 seconds
      duration: 10000, // Smooth transition over 10s
    },
  },
} as const;

/**
 * Type-safe route identifiers for map configuration
 */
export enum MapRoute {
  HOME = "home",
  BROWSE = "browse",
  TITLE = "title",
  SIGNIN = "signin",
  SIGNUP = "signup",
  NONE = "none",
}

/**
 * Determine which route-specific configuration to apply based on pathname
 */
export function getMapRouteFromPathname(pathname: string): MapRoute {
  if (pathname === "/home") {
    return MapRoute.HOME;
  }
  if (pathname.includes("/browse")) {
    return MapRoute.BROWSE;
  }
  if (pathname.includes("/title/")) {
    return MapRoute.TITLE;
  }
  if (pathname === "/sign-in") {
    return MapRoute.SIGNIN;
  }
  if (pathname === "/sign-up") {
    return MapRoute.SIGNUP;
  }
  return MapRoute.NONE;
}

/**
 * Check if the current pathname is the map/browse page
 * This is the only page that shows interactive country boundaries
 */
export function isBrowsePage(pathname: string): boolean {
  return pathname.includes("/browse");
}

/**
 * Get opacity setting for current route
 */
export function getOpacityForRoute(route: MapRoute): number {
  switch (route) {
    case MapRoute.HOME:
      return MAPBOX_CONFIG.opacity.homepage;
    case MapRoute.BROWSE:
      return MAPBOX_CONFIG.opacity.browse;
    case MapRoute.TITLE:
      return MAPBOX_CONFIG.opacity.titlePage;
    case MapRoute.SIGNIN:
      return MAPBOX_CONFIG.opacity.signin; // Full brightness for signin
    case MapRoute.SIGNUP:
      return MAPBOX_CONFIG.opacity.signup; // Full brightness for signup
    default:
      return 0; // Hidden on other routes
  }
}

/**
 * Get zoom setting for current route
 */
export function getZoomForRoute(route: MapRoute): number {
  switch (route) {
    case MapRoute.HOME:
      return MAPBOX_CONFIG.zoom.homepage;
    case MapRoute.BROWSE:
      return MAPBOX_CONFIG.zoom.browse;
    case MapRoute.SIGNIN:
    case MapRoute.SIGNUP:
      return MAPBOX_CONFIG.zoom.homepage; // Same zoom as homepage
    default:
      return MAPBOX_CONFIG.defaultView.zoom;
  }
}

/**
 * Get center position for current route
 */
export function getCenterForRoute(route: MapRoute): [number, number] {
  switch (route) {
    case MapRoute.HOME:
    case MapRoute.SIGNIN:
    case MapRoute.SIGNUP:
      return MAPBOX_CONFIG.center.homepage;
    default:
      return MAPBOX_CONFIG.center.default;
  }
}
